import { useRef, useState } from "react";
import { api } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { UploadCloud, FileText, CalendarClock, Type } from "lucide-react";

const BATCH_SIZE = 50;

const AdminImport = () => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, imported: 0, failed: 0 });
  const [running, setRunning] = useState(false);
  const [errors, setErrors] = useState([]);
  const [backfill, setBackfill] = useState(null); // preview/result
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [titles, setTitles] = useState(null);
  const [titlesBusy, setTitlesBusy] = useState(false);

  const runBackfill = async (dryRun) => {
    setBackfillBusy(true);
    const tid = toast.loading(dryRun ? "anteprima in corso…" : "aggiornamento date…");
    try {
      const { data } = await api.post("/admin/backfill-dates", { dry_run: dryRun });
      setBackfill(data);
      toast.success(
        dryRun
          ? `${data.updated} date riconosciute, ${data.not_found} senza data`
          : `${data.updated} date aggiornate`,
        { id: tid }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore", { id: tid });
    } finally {
      setBackfillBusy(false);
    }
  };

  const runCleanTitles = async (dryRun, forceAll) => {
    setTitlesBusy(true);
    const tid = toast.loading(dryRun ? "anteprima in corso…" : "aggiornamento titoli…");
    try {
      const { data } = await api.post("/admin/clean-titles", { dry_run: dryRun, force_all: forceAll });
      setTitles(data);
      toast.success(
        dryRun
          ? `${data.updated} titoli da ripulire`
          : `${data.updated} titoli aggiornati`,
        { id: tid }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore", { id: tid });
    } finally {
      setTitlesBusy(false);
    }
  };

  const onPick = (e) => {
    const list = Array.from(e.target.files || []).filter((f) => f.name.toLowerCase().endsWith(".rtf"));
    setFiles(list);
    setProgress({ done: 0, total: list.length, imported: 0, failed: 0 });
    setErrors([]);
  };

  const start = async () => {
    if (!files.length) return;
    setRunning(true);
    setProgress({ done: 0, total: files.length, imported: 0, failed: 0 });
    setErrors([]);
    let imported = 0, failed = 0, done = 0;
    const allErrors = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      const fd = new FormData();
      batch.forEach((f) => fd.append("files", f));
      try {
        const { data } = await api.post("/admin/import-rtf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imported += data.imported || 0;
        failed += data.failed || 0;
        if (data.errors?.length) allErrors.push(...data.errors);
      } catch (e) {
        failed += batch.length;
        allErrors.push(e?.response?.data?.detail || e.message);
      }
      done += batch.length;
      setProgress({ done, total: files.length, imported, failed });
    }
    setErrors(allErrors.slice(0, 30));
    setRunning(false);
    toast.success(`Importati ${imported} post (${failed} errori)`);
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <AdminShell title="Importa RTF">
      <div className="max-w-3xl">
        <p className="font-serif italic text-lg text-[#4a4843] mb-10 leading-relaxed">
          Carica una cartella di file <code className="font-sans bg-[#f2efe9] px-1.5 py-0.5">.rtf</code>: ogni file diventerà un post in bozza. Il titolo sarà la prima riga (se breve) o il nome del file. Potrai poi rifinire e pubblicare.
        </p>

        <div
          className="border-2 border-dashed border-[#1a1918] p-8 sm:p-12 text-center bg-[#f2efe9]/50 hover:bg-[#f2efe9] transition-colors cursor-pointer"
          onClick={() => inputRef.current?.click()}
          data-testid="import-dropzone"
        >
          <UploadCloud size={32} className="mx-auto text-[#4a4843] mb-4" />
          <p className="font-serif text-xl sm:text-2xl text-[#1a1918] mb-2">Seleziona file RTF</p>
          <p className="font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#4a4843]">
            anche un'intera cartella · solo .rtf
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".rtf"
            multiple
            webkitdirectory=""
            directory=""
            onChange={onPick}
            className="hidden"
            data-testid="import-input"
          />
        </div>

        {!!files.length && (
          <div className="mt-8 border border-[#e5e1d8] p-4 sm:p-6 bg-[#f9f8f6]" data-testid="import-summary">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <p className="font-serif text-xl sm:text-2xl text-[#1a1918]">{files.length} file pronti</p>
              <button
                data-testid="import-start"
                onClick={start}
                disabled={running}
                className="bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-5 sm:px-6 py-3 font-sans text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase disabled:opacity-50"
              >
                {running ? "importazione…" : "avvia importazione"}
              </button>
            </div>
            <div className="h-1 bg-[#e5e1d8] mb-3 overflow-hidden">
              <div
                className="h-full bg-[#d4b886] transition-all duration-300"
                style={{ width: `${pct}%` }}
                data-testid="import-progress"
              />
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-1 font-sans text-xs tracking-[0.2em] uppercase text-[#4a4843]">
              <span>processati: {progress.done} / {progress.total}</span>
              <span className="text-[#477d55]">importati: {progress.imported}</span>
              {progress.failed > 0 && <span className="text-[#9e4747]">errori: {progress.failed}</span>}
            </div>

            {!running && files.length > 0 && progress.done === 0 && (
              <ul className="mt-6 max-h-64 overflow-auto divide-y divide-[#e5e1d8]" data-testid="import-file-list">
                {files.slice(0, 100).map((f) => (
                  <li key={f.name} className="py-2 flex items-center gap-2 font-sans text-xs text-[#4a4843]">
                    <FileText size={12} /> {f.name}
                  </li>
                ))}
                {files.length > 100 && (
                  <li className="py-2 font-sans text-xs italic text-[#4a4843]">…e altri {files.length - 100} file</li>
                )}
              </ul>
            )}

            {!!errors.length && (
              <div className="mt-6">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#9e4747] mb-2">Errori</p>
                <ul className="font-sans text-xs text-[#4a4843] max-h-40 overflow-auto">
                  {errors.map((er, i) => <li key={i} className="py-1 border-b border-[#e5e1d8]">{er}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* --- Backfill dates from filenames --- */}
        <div className="mt-12 sm:mt-16 pt-8 sm:pt-10 border-t border-[#e5e1d8]">
          <div className="flex items-center gap-3 mb-4">
            <CalendarClock size={18} className="text-[#c5a873]" />
            <h2 className="font-serif text-xl sm:text-2xl text-[#1a1918]">
              Recupera date dai nomi file
            </h2>
          </div>
          <p className="font-serif italic text-[#4a4843] mb-6 leading-relaxed max-w-2xl">
            Molti dei tuoi RTF hanno nel nome una data (es. <code className="font-sans not-italic bg-[#f2efe9] px-1.5">2019-07-14_pensiero.rtf</code> o <code className="font-sans not-italic bg-[#f2efe9] px-1.5">14-07-2019 pensiero.rtf</code>). Questa funzione legge il nome di ogni post importato e, se trova una data valida, la imposta come data del post. I file senza data restano con la data di importazione e sono elencati qui sotto.
          </p>

          <div className="flex flex-wrap gap-3 mb-6">
            <button
              data-testid="btn-backfill-preview"
              onClick={() => runBackfill(true)}
              disabled={backfillBusy}
              className="border border-[#1a1918] text-[#1a1918] hover:bg-[#f2efe9] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              anteprima
            </button>
            <button
              data-testid="btn-backfill-apply"
              onClick={() => {
                if (!window.confirm("Aggiornare tutte le date dei post in base ai nomi file? L'operazione sovrascrive la data di creazione ma è reversibile solo post per post.")) return;
                runBackfill(false);
              }}
              disabled={backfillBusy}
              className="bg-[#1a1918] text-[#f9f8f6] hover:bg-[#c5a873] hover:text-[#1a1918] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              applica
            </button>
          </div>

          {backfill && (
            <div data-testid="backfill-result" className="border border-[#e5e1d8] bg-[#f9f8f6] p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div data-testid="backfill-updated">
                  <div className="font-serif text-3xl text-[#477d55] font-light">{backfill.updated}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">
                    {backfill.dry_run ? "da aggiornare" : "aggiornati"}
                  </div>
                </div>
                <div data-testid="backfill-not-found">
                  <div className="font-serif text-3xl text-[#9e4747] font-light">{backfill.not_found}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">senza data nel nome</div>
                </div>
                <div data-testid="backfill-skipped">
                  <div className="font-serif text-3xl text-[#4a4843] font-light">{backfill.skipped_no_filename}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">senza filename</div>
                </div>
              </div>

              {!!backfill.samples_updated?.length && (
                <div>
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#477d55] mb-2">Esempi di date riconosciute</p>
                  <ul className="font-sans text-xs text-[#4a4843] divide-y divide-[#e5e1d8]">
                    {backfill.samples_updated.map((s, i) => (
                      <li key={i} className="py-1.5 flex gap-3 justify-between">
                        <span className="truncate">{s.filename}</span>
                        <span className="font-serif italic text-[#1a1918] shrink-0">{s.new_date}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!!backfill.samples_not_found?.length && (
                <div>
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#9e4747] mb-2">Esempi di file senza data nel nome</p>
                  <ul className="font-sans text-xs text-[#4a4843] divide-y divide-[#e5e1d8]">
                    {backfill.samples_not_found.map((s, i) => (
                      <li key={i} className="py-1.5 truncate">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- Clean titles --- */}
        <div className="mt-12 sm:mt-16 pt-8 sm:pt-10 border-t border-[#e5e1d8]">
          <div className="flex items-center gap-3 mb-4">
            <Type size={18} className="text-[#c5a873]" />
            <h2 className="font-serif text-xl sm:text-2xl text-[#1a1918]">
              Ripulisci titoli
            </h2>
          </div>
          <p className="font-serif italic text-[#4a4843] mb-6 leading-relaxed max-w-2xl">
            Alcuni post importati hanno come titolo il nome del file (es. <code className="font-sans not-italic bg-[#f2efe9] px-1.5">Notability/# 16 mar 2026 12_29_38.rtf</code>). Se nel nome c'è una data/ora riconoscibile, il titolo viene sostituito con una forma leggibile tipo <code className="font-sans not-italic bg-[#f2efe9] px-1.5">16 marzo 2026 · 12:29:38</code>. I titoli già "puliti" vengono lasciati com'erano.
          </p>

          <div className="flex flex-wrap gap-3 mb-6">
            <button
              data-testid="btn-titles-preview"
              onClick={() => runCleanTitles(true, false)}
              disabled={titlesBusy}
              className="border border-[#1a1918] text-[#1a1918] hover:bg-[#f2efe9] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              anteprima
            </button>
            <button
              data-testid="btn-titles-apply"
              onClick={() => {
                if (!window.confirm("Ripulire i titoli che sembrano nomi di file? Verrà sostituito il titolo con 'GG mese AAAA · HH:MM:SS' dove possibile.")) return;
                runCleanTitles(false, false);
              }}
              disabled={titlesBusy}
              className="bg-[#1a1918] text-[#f9f8f6] hover:bg-[#c5a873] hover:text-[#1a1918] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              applica (solo quelli che sembrano filename)
            </button>
            <button
              data-testid="btn-titles-apply-all"
              onClick={() => {
                if (!window.confirm("ATTENZIONE: ripulire il titolo di TUTTI i post che hanno una data nel nome file, anche se il titolo attuale sembra già buono? Azione drastica e irreversibile in blocco.")) return;
                runCleanTitles(false, true);
              }}
              disabled={titlesBusy}
              className="border border-[#9e4747] text-[#9e4747] hover:bg-[#9e4747] hover:text-[#f9f8f6] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              forza tutti
            </button>
          </div>

          {titles && (
            <div data-testid="titles-result" className="border border-[#e5e1d8] bg-[#f9f8f6] p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <div className="font-serif text-3xl text-[#477d55] font-light">{titles.updated}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">
                    {titles.dry_run ? "da ripulire" : "ripuliti"}
                  </div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-[#4a4843] font-light">{titles.skipped_title_ok}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">già buoni</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-[#9e4747] font-light">{titles.skipped_no_date}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">no data</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-[#4a4843] font-light">{titles.skipped_no_filename}</div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mt-1">no filename</div>
                </div>
              </div>

              {!!titles.samples_updated?.length && (
                <div>
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#477d55] mb-2">Esempi di sostituzione</p>
                  <ul className="font-sans text-xs text-[#4a4843] divide-y divide-[#e5e1d8]">
                    {titles.samples_updated.map((s, i) => (
                      <li key={i} className="py-2">
                        <div className="text-[#9e4747] line-through truncate">{s.old || "(senza titolo)"}</div>
                        <div className="font-serif italic text-[#1a1918]">→ {s.new}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminImport;
